import type { VisualPlacement } from '@argink/shared/owner-web';

/**
 * **Carimbo visível — presets de posição (D-027, #101a).**
 *
 * O contrato (`zVisualPlacement`) fala em frações da página: `x`/`y` são o canto SUPERIOR
 * ESQUERDO do carimbo, `width`/`height` o tamanho, todos entre 0 e 1, com origem no topo à
 * esquerda. Isso é a linguagem do motor, não a do responsável pelo documento — ninguém escolhe
 * "0.62 / 0.84" numa tela.
 *
 * Esta rodada expõe a capacidade que já existe (motor + banco + `/v1`) por **seis posições
 * nomeadas**, e nada além disso: não há arrastar-e-soltar nem prévia do PDF. Escolher uma
 * posição fixa é honesto (o carimbo cai exatamente ali); prometer posicionamento livre sem
 * renderizar o documento não seria. O refinamento visual é rodada futura, com o PDF em tela.
 *
 * As frações são **literais**, não calculadas: `1 - 0.06 - 0.32` em ponto flutuante daria
 * `0.6200000000000001` viajando no JSON. Os literais abaixo são a mesma geometria, escrita de
 * forma exata — e é isso que faz o caminho de volta (`stampPresetOf`) casar por igualdade.
 *
 * Geometria: carimbo de 32% da largura por 10% da altura, com margem de 6% das bordas.
 */

export const STAMP_PRESET_VALUES = [
  'bottom-left',
  'bottom-center',
  'bottom-right',
  'top-left',
  'top-center',
  'top-right',
] as const;
export type StampPreset = (typeof STAMP_PRESET_VALUES)[number];

/** Posição default do opt-in: rodapé à direita, onde a assinatura manuscrita costuma ir. */
export const STAMP_DEFAULT_PRESET: StampPreset = 'bottom-right';
export const STAMP_DEFAULT_PAGE = 1;

export const STAMP_WIDTH = 0.32;
export const STAMP_HEIGHT = 0.1;

/** `x` por coluna: 0.06 (margem), 0.34 (centrado: `(1 - 0.32) / 2`), 0.62 (`1 - 0.06 - 0.32`). */
const COLUMN_X = { left: 0.06, center: 0.34, right: 0.62 } as const;
/** `y` por linha: 0.06 (margem do topo) e 0.84 (`1 - 0.06 - 0.10`). */
const ROW_Y = { top: 0.06, bottom: 0.84 } as const;

const STAMP_PRESET_ORIGIN: Record<StampPreset, { x: number; y: number }> = {
  'bottom-left': { x: COLUMN_X.left, y: ROW_Y.bottom },
  'bottom-center': { x: COLUMN_X.center, y: ROW_Y.bottom },
  'bottom-right': { x: COLUMN_X.right, y: ROW_Y.bottom },
  'top-left': { x: COLUMN_X.left, y: ROW_Y.top },
  'top-center': { x: COLUMN_X.center, y: ROW_Y.top },
  'top-right': { x: COLUMN_X.right, y: ROW_Y.top },
};

/** Preset + página → o posicionamento em frações que o contrato `/web` transporta. */
export function stampPlacement(preset: StampPreset, page: number): VisualPlacement {
  const origin = STAMP_PRESET_ORIGIN[preset];
  return { page, x: origin.x, y: origin.y, width: STAMP_WIDTH, height: STAMP_HEIGHT };
}

/**
 * Caminho de volta: frações → preset, por igualdade exata com a tabela acima.
 *
 * `null` quando o posicionamento não é nenhum dos seis — o que só acontece com envelope montado
 * por outro cliente (a `/v1` aceita qualquer geometria válida). Quem chama decide o que fazer
 * com isso; o que este módulo NÃO faz é escolher o preset "mais próximo" e deixar a tela afirmar
 * uma posição que não é a gravada.
 */
export function stampPresetOf(placement: VisualPlacement): StampPreset | null {
  if (placement.width !== STAMP_WIDTH || placement.height !== STAMP_HEIGHT) return null;
  return (
    STAMP_PRESET_VALUES.find((preset) => {
      const origin = STAMP_PRESET_ORIGIN[preset];
      return origin.x === placement.x && origin.y === placement.y;
    }) ?? null
  );
}
